// !@TODO: REWRITE THIS. Whoever thought of this idea is an evil, evil person.

/* eslint-disable fp/no-loops */
import type { Editor } from '@tiptap/core';
import lodash from 'lodash';
import { isDefined } from 'ts-is-present';

import { getTextDirection } from './../../ricos-content';
import type { IEditorQuery } from './../../ricos-content-query';
import { Node_Type } from './../../ricos-schema/legacy/wix/rich_content/v1';
import type { RicosStyles } from './../../ricos-styles';
import type { HeadingNode, ParagraphNode, RichTextNode } from './../../ricos-types';
import { Decoration_Type, Mark_Type } from './../../ricos-types';

import { TiptapContentResolver } from '../ContentResolver';
import { RESOLVERS_IDS } from './resolvers-ids';

type EditorQueryTextAlignment = 'LEFT' | 'CENTER' | 'RIGHT' | 'JUSTIFY' | 'AUTO';

export const alwaysVisibleResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.ALWAYS_VISIBLE,
  () => {
    return true;
  }
);

export const alwaysActiveResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.ALWAYS_ACTIVE,
  () => {
    return true;
  }
);

export const isTextInSelection = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_IN_SELECTION,
  (content = []) => {
    return content.some(node => node.type.name === 'text');
  }
);

export const isTextContainsBoldResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_CONTAINS_BOLD,
  (
    content,
    { getEditorQuery }: { styles: RicosStyles; converters: $TSFixMe; getEditorQuery: any }
  ) => {
    const editorQuery: IEditorQuery = getEditorQuery();

    const decorations = editorQuery.query.activeDecorationsByType(Decoration_Type.BOLD);
    return (
      decorations.length > 0 && decorations.every((item: $TSFixMe) => item?.fontWeightValue === 700)
    );
  }
);

export const isTextContainsItalicResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_CONTAINS_ITALIC,
  (content, { styles, converters }, editor) => {
    if (Array.isArray(content)) {
      const node = content.find(node => {
        return node.type.name === 'text';
      });
      let hasItalicDecorationInDocumentStyles = false;
      if (node && styles && converters) {
        hasItalicDecorationInDocumentStyles = !!content.find(node => {
          const decoration = styles.getDecoration(
            converters.fromPmNode(node.toJSON()) as RichTextNode,
            Decoration_Type.ITALIC
          );
          return decoration.italicData;
        });
      }

      const hasInlineItalic = node?.marks.some(mark => {
        return mark.type.name === Decoration_Type.ITALIC && mark.attrs.italicData !== false;
      });

      const hasInlineUnsetItalic = node?.marks.some(mark => {
        return mark.type.name === Decoration_Type.ITALIC && mark.attrs.italicData === false;
      });

      if (hasInlineUnsetItalic) {
        return false;
      }

      if (editor?.isActive(Decoration_Type.ITALIC, { italicData: true })) {
        return true;
      }

      return hasInlineItalic || hasItalicDecorationInDocumentStyles;
    }
    return false;
  }
);

export const isTextContainsUnderlineResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_CONTAINS_UNDERLINE,
  (content, { getEditorQuery }, _editor) => {
    const editorQuery: IEditorQuery = getEditorQuery();
    const decorations = editorQuery.query.activeDecorationsByType(Decoration_Type.UNDERLINE);
    return (
      decorations.length > 0 &&
      decorations.every((item: $TSFixMe) => item?.type === Decoration_Type.UNDERLINE)
    );
  }
);

export const isTextContainsQuoteResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_CONTAINS_QUOTE,
  content => {
    if (Array.isArray(content)) {
      const node = content.find(node => node.type.name === Node_Type.BLOCKQUOTE);
      return Boolean(node);
    }

    return false;
  }
);

export const isTextContainsCodeblockResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_CONTAINS_CODE_BLOCK,
  content => {
    return (
      Array.isArray(content) && content.length > 0 && content[0].type.name === Node_Type.CODE_BLOCK
    );
  }
);

export const isTextContainsOrderedListResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_CONTAINS_ORDERED_LIST,
  content => {
    return (
      Array.isArray(content) &&
      content.length > 0 &&
      content[0].type.name === Node_Type.ORDERED_LIST
    );
  }
);

export const isTextContainsUnorderedListResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_CONTAINS_UNORDERED_LIST,
  content => {
    return (
      Array.isArray(content) &&
      content.length > 0 &&
      content[0].type.name === Node_Type.BULLETED_LIST
    );
  }
);

export const isTextContainsSpoilerResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_CONTAINS_SPOILER,
  (content, { getEditorQuery }, _editor) => {
    const editorQuery: IEditorQuery = getEditorQuery();
    const decorations = editorQuery.query.activeDecorationsByType(Decoration_Type.SPOILER);
    return (
      decorations.length > 0 &&
      decorations.every((item: $TSFixMe) => item?.type === Decoration_Type.SPOILER)
    );
  }
);

const getTextAlignment = (activeTextAlignment: EditorQueryTextAlignment, editor: Editor) => {
  const textAlignment = activeTextAlignment.toLowerCase();
  if (textAlignment !== 'auto') return textAlignment;
  const {
    state: {
      doc,
      selection: { from },
    },
  } = editor;

  const nodeToCheck = doc.nodeAt(from);
  const isTextNode = nodeToCheck?.type.name === 'text';
  const isEmptyLine = nodeToCheck?.type.name === undefined;
  if (!(isTextNode || isEmptyLine)) return;

  const selectedFirstNodeText = doc.nodeAt(from - 1)?.textContent;
  const selectedFirstNodeDirection = getTextDirection(selectedFirstNodeText);
  return selectedFirstNodeDirection === 'rtl' ? 'right' : 'left';
};

export const getAlignmentInSelectionResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_ALIGNMENT_IN_SELECTION,
  (_content, { getEditorQuery }, editor) => {
    const editorQuery: IEditorQuery = getEditorQuery();
    const activeTextStyles = editorQuery.query.activeTextStyles('textAlignment');
    if (editor && activeTextStyles.length > 0 && lodash.uniq(activeTextStyles).length === 1) {
      return getTextAlignment(activeTextStyles[0], editor);
    }
    return undefined;
  }
);

export const isTextAlignLeft = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_ALIGN_LEFT,
  (_content, { getEditorQuery }, editor) => {
    const editorQuery: IEditorQuery = getEditorQuery();
    const activeTextStyles = editorQuery.query.activeTextStyles('textAlignment');
    if (editor && activeTextStyles.length > 0 && lodash.uniq(activeTextStyles).length === 1) {
      return getTextAlignment(activeTextStyles[0], editor) === 'left';
    }
    return undefined;
  }
);

export const isTextAlignCenter = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_ALIGN_CENTER,
  (_content, { getEditorQuery }, editor) => {
    const editorQuery: IEditorQuery = getEditorQuery();
    const activeTextStyles = editorQuery.query.activeTextStyles('textAlignment');
    if (editor && activeTextStyles.length > 0 && lodash.uniq(activeTextStyles).length === 1) {
      return getTextAlignment(activeTextStyles[0], editor) === 'center';
    }
    return undefined;
  }
);

export const isTextAlignRight = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_ALIGN_RIGHT,
  (_content, { getEditorQuery }, editor) => {
    const editorQuery: IEditorQuery = getEditorQuery();
    const activeTextStyles = editorQuery.query.activeTextStyles('textAlignment');
    if (editor && activeTextStyles.length > 0 && lodash.uniq(activeTextStyles).length === 1) {
      return getTextAlignment(activeTextStyles[0], editor) === 'right';
    }
    return undefined;
  }
);

export const isTextAlignJustify = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_ALIGN_JUSTIFY,
  (_content, { getEditorQuery }, editor) => {
    const editorQuery: IEditorQuery = getEditorQuery();
    const activeTextStyles = editorQuery.query.activeTextStyles('textAlignment');
    if (editor && activeTextStyles.length > 0 && lodash.uniq(activeTextStyles).length === 1) {
      return getTextAlignment(activeTextStyles[0], editor) === 'justify';
    }
    return undefined;
  }
);

export const getHeadingInSelectionResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_HEADING_IN_SELECTION,
  content => {
    if (Array.isArray(content) && content.length > 0) {
      const headingLevel = content.find(node => node.type.name === Node_Type.HEADING)?.attrs?.level;
      if (!headingLevel || content[0].type.name === Node_Type.PARAGRAPH) return 'unstyled';
      const headingMap = {
        1: 'header-one',
        2: 'header-two',
        3: 'header-three',
        4: 'header-four',
        5: 'header-five',
        6: 'header-six',
      };
      return headingMap[headingLevel as keyof typeof headingMap];
    } else {
      return undefined;
    }
  }
);

export const isHeaderTwoOrThreeSelected = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_HEADER_TWO_OR_THREE_SELECTED,
  content => {
    if (
      Array.isArray(content) &&
      content.length > 0 &&
      content[0].type.name !== Node_Type.PARAGRAPH
    ) {
      const headingLevel = content[0].attrs?.level;
      return headingLevel === 2 || headingLevel === 3;
    } else {
      return false;
    }
  }
);

export const isTextStylesMatchDocumentStylesResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_STYLES_MATCH_DOCUMENT_STYLES,
  (content = [], { styles, converters }) => {
    const currentNode = content.filter(node => {
      return node.type.name === Node_Type.HEADING || node.type.name === Node_Type.PARAGRAPH;
    });

    let stylesMatched = true;
    if (currentNode.length === 1) {
      const ricosCurrentNode = converters.fromPmNode(currentNode[0].toJSON());

      const currentNodeDocumentsStyles = styles
        .getDocumentStyle()
        .getDecorations(ricosCurrentNode as ParagraphNode | HeadingNode);
      const textsDecorations = (ricosCurrentNode?.nodes ?? [])
        .filter(node => {
          return node.type === Node_Type.TEXT;
        })
        .map(node => {
          return node.textData?.decorations;
        })
        .flat()
        .filter(isDefined);

      stylesMatched = textsDecorations.every(decoration => {
        const currentDocumentStyleDecoration = currentNodeDocumentsStyles.find(documentStyle => {
          return documentStyle.type === decoration.type;
        });
        if (currentDocumentStyleDecoration) {
          const removeNullReplacer = (key: $TSFixMe, value: $TSFixMe) =>
            value === null ? undefined : value;

          const cleanDecoration = JSON.parse(JSON.stringify(decoration, removeNullReplacer));
          const cleaneDcurrentDocumentStyleDecoration = JSON.parse(
            JSON.stringify(currentDocumentStyleDecoration, removeNullReplacer)
          );
          if (currentDocumentStyleDecoration) {
            if (!lodash.isEqual(cleaneDcurrentDocumentStyleDecoration, cleanDecoration)) {
              return false;
            }
          }
        } else {
          return false;
        }

        return true;
      });
    }

    return stylesMatched;
  }
);

export const getLineSpacingInSelectionResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_LINE_SPACING_IN_SELECTION,
  (_content, { getEditorQuery }) => {
    const editorQuery: IEditorQuery = getEditorQuery();
    const activeTextStyles = editorQuery.query.activeTextStyles('lineHeight');

    if (
      activeTextStyles.length > 0 &&
      activeTextStyles.every((lineHeight: $TSFixMe, _index: $TSFixMe, arr: $TSFixMe) => {
        return lineHeight === arr[0];
      })
    ) {
      return activeTextStyles[0].toString();
    } else {
      return '';
    }
  }
);

export const getLineSpacingBeforeSelectionResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_LINE_SPACING_BEFORE_SELECTION,
  content => {
    if (Array.isArray(content) && content.length > 0) {
      const node = content.find(node => {
        if (!node?.attrs) {
          return false;
        }
        const { style, styleDefaults } = node.attrs;
        return style?.paddingTop || styleDefaults?.paddingTop;
      });

      return node?.attrs?.style?.paddingTop ?? node?.attrs?.styleDefaults?.paddingTop;
    }
  }
);

export const getLineSpacingAfterSelectionResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_LINE_SPACING_AFTER_SELECTION,
  content => {
    if (Array.isArray(content) && content.length > 0) {
      const lastWithPadding = content
        .filter(node => {
          if (!node?.attrs) {
            return false;
          }
          const { style, styleDefaults } = node.attrs;
          return style?.paddingBottom || styleDefaults?.paddingBottom;
        })
        .pop();

      return (
        lastWithPadding?.attrs.style?.paddingBottom ??
        lastWithPadding?.attrs.styleDefaults?.paddingBottom
      );
    }
  }
);

const MANY_FONT_SIZES = '';
// Probably we should break instead of returning this one
const UNKNOWN_FONT_SIZE = '';
export const getFontSizeInSelectionResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_FONT_SIZE_IN_SELECTION,
  (_content, { styles, converters }, editor): string => {
    const getDefaultParagraphValue = (nodeDom?: HTMLElement) => {
      const decoration = styles.getDecoration(
        {
          paragraphData: {},
          type: Node_Type.PARAGRAPH,
        } as RichTextNode,
        Decoration_Type.FONT_SIZE,
        nodeDom
      );
      return decoration.fontSizeData?.value?.toString() ?? UNKNOWN_FONT_SIZE;
    };

    if (!editor) {
      // @TODO: fix editor type for the resolvers
      return getDefaultParagraphValue();
    }
    const { state } = editor;

    const { from, $from, to, empty } = state.selection;
    if (empty) {
      // From stored marks
      const storedFontSize = state.storedMarks?.find(
        m => m.type.name === Decoration_Type.FONT_SIZE
      );
      if (storedFontSize?.attrs?.value) {
        return storedFontSize.attrs.value.toString();
      }
      // From mark under cursor
      const markUnderCursor = $from.marks().find(m => m.type.name === Decoration_Type.FONT_SIZE);
      if (markUnderCursor?.attrs?.value) {
        return markUnderCursor.attrs.value.toString();
      }
      // From parent block node
      const parent = state.doc.resolve(from).parent;
      const ricosNode = converters.fromPmNode({
        ...parent.toJSON(),
        type: parent?.type?.name,
      });
      const decoration = styles.getDecoration(ricosNode as RichTextNode, Decoration_Type.FONT_SIZE);

      if (decoration.fontSizeData?.value) {
        const fontSizeInDocumentStyle = decoration.fontSizeData?.value;
        return fontSizeInDocumentStyle.toString();
      }
      // @TODO: should we throw an error here?
      return UNKNOWN_FONT_SIZE;
    }

    const selectedFontSizes: string[] = [];

    state.doc.nodesBetween(from, to, (node, pos) => {
      if (node.type.name === 'text') {
        const fontSizeMark = node.marks.find(mark => mark.type.name === Decoration_Type.FONT_SIZE);
        if (fontSizeMark) {
          selectedFontSizes.push(`${fontSizeMark?.attrs.value}`);
        } else {
          const parent = state.doc.resolve(pos).parent;
          const ricosNode = converters.fromPmNode({
            ...parent.toJSON(),
            type: parent?.type?.name,
          });
          const decoration = styles.getDecoration(
            ricosNode as RichTextNode,
            Decoration_Type.FONT_SIZE
          );

          if (decoration.fontSizeData?.value) {
            const fontSizeInDocumentStyle = decoration.fontSizeData?.value;
            selectedFontSizes.push(`${fontSizeInDocumentStyle}`);
          }
        }
      }
    });

    if (selectedFontSizes.length === 0) {
      const nodeDom = editor.view.nodeDOM(from);
      const el = nodeDom instanceof HTMLElement ? nodeDom : nodeDom?.parentElement;
      return getDefaultParagraphValue(el as HTMLElement);
    }
    return selectedFontSizes.every(fontSize => fontSize === selectedFontSizes[0])
      ? selectedFontSizes[0]
      : MANY_FONT_SIZES;
  }
);

export const isTextContainsLinkOrAnchorResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_CONTAINS_LINK_OR_ANCHOR,
  content => {
    if (Array.isArray(content)) {
      const node = content.find(node => {
        return node.type.name === 'text';
      });
      return node?.marks.some(
        mark => mark.type.name === Decoration_Type.LINK || mark.type.name === Decoration_Type.ANCHOR
      );
    }
    return false;
  }
);

export const isTextContainsLinkResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_CONTAINS_LINK,
  content => {
    if (Array.isArray(content)) {
      const node = content.find(node => {
        return node.type.name === 'text';
      });
      return node?.marks.some(mark => mark.type.name === Decoration_Type.LINK);
    }
    return false;
  }
);

export const isTextContainsAnchorResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_CONTAINS_ANCHOR,
  content => {
    if (Array.isArray(content)) {
      const node = content.find(node => {
        return node.type.name === 'text';
      });
      return node?.marks.some(mark => mark.type.name === Decoration_Type.ANCHOR);
    }
    return false;
  }
);

export const getTextColorInSelectionResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_TEXT_COLOR_IN_SELECTION,
  (content, { styles, converters }, editor) => {
    if (Array.isArray(content)) {
      const node = content.find(node => {
        return node.type.name === 'text';
      });
      const colorMark = node?.marks.find(mark => mark.type.name === Mark_Type.COLOR_FOREGROUND);

      let textColorInDocumentStyle;
      if (node && styles && converters) {
        for (const node of content) {
          const decoration = styles.getDecoration(
            converters.fromPmNode(node.toJSON()) as RichTextNode,
            Decoration_Type.COLOR
          );
          if (decoration.colorData?.foreground) {
            textColorInDocumentStyle = decoration.colorData?.foreground;
            break;
          }
        }
      }

      const storedMark = editor?.state?.storedMarks?.find(mark => {
        return mark.type.name === Mark_Type.COLOR_FOREGROUND && mark.attrs.color;
      });

      const markInSelection = editor?.state?.selection?.$from.marks().find(mark => {
        return mark.type.name === Mark_Type.COLOR_FOREGROUND && mark.attrs.color;
      });

      let colorInStoredMark;
      let colorInSelection;
      if (storedMark) {
        colorInStoredMark = storedMark.attrs.color;
      }

      if (markInSelection) {
        colorInSelection = markInSelection.attrs.color;
      }

      return (
        colorMark?.attrs.color ||
        textColorInDocumentStyle ||
        colorInStoredMark ||
        colorInSelection ||
        styles.getTheme().getColors()?.textColor
      );
    }
    return false;
  }
);

export const getHighlightColorInSelectionResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_HIGHLIGHT_COLOR_IN_SELECTION,
  (content, { styles, converters }, editor) => {
    if (Array.isArray(content)) {
      const node = content.find(node => {
        return node.type.name === 'text';
      });
      let highlightColorInDocumentStyle;
      if (node && styles && converters) {
        for (const node of content) {
          const decoration = styles.getDecoration(
            converters.fromPmNode(node.toJSON()) as RichTextNode,
            Decoration_Type.COLOR
          );
          if (decoration.colorData?.background) {
            highlightColorInDocumentStyle = decoration.colorData?.background;
            break;
          }
        }
      }
      const storedMark = editor?.state?.storedMarks?.find(mark => {
        return mark.type.name === Mark_Type.COLOR_BACKGROUND && mark.attrs.color;
      });
      const markInSelection = editor?.state?.selection?.$from.marks().find(mark => {
        return mark.type.name === Mark_Type.COLOR_BACKGROUND && mark.attrs.color;
      });

      let colorInStoredMark, colorInSelection;
      if (storedMark) {
        colorInStoredMark = storedMark.attrs.color;
      }

      if (markInSelection) {
        colorInSelection = markInSelection.attrs.color;
      }
      const colorMark = node?.marks.find(mark => mark.type.name === Mark_Type.COLOR_BACKGROUND);
      return (
        colorMark?.attrs.color ||
        highlightColorInDocumentStyle ||
        colorInStoredMark ||
        colorInSelection ||
        styles.getTheme().getColors()?.bgColor
      );
    }
    return false;
  }
);

export const isNodeContainsLinkOrAnchorResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_NODE_CONTAINS_LINK_OR_ANCHOR,
  (_, __, editor) => {
    const node = (editor?.state?.selection as Record<string, any> | undefined)?.node;
    if (node) {
      return node.attrs?.link;
    } else {
      return undefined;
    }
  }
);

export const isNodeSelected = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_NODE_SELECTED,
  (_, __, editor) => {
    const node = (editor?.state?.selection as Record<string, any> | undefined)?.node;
    return !!node;
  }
);

export const isNodeOrCaptionSelected = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_NODE_OR_CAPTION_SELECTED,
  (_, __, editor) => {
    const node = (editor?.state?.selection as Record<string, any> | undefined)?.node;
    const isCaption =
      editor?.state?.selection.$anchor.parent.type.name === Node_Type.CAPTION ||
      editor?.state?.selection.$head.parent.type.name === Node_Type.CAPTION;

    return !!node || isCaption;
  }
);

export const getNodeInSelectionResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_NODE_IN_SELECTION,
  (_, __, editor) => {
    const node = (editor?.state?.selection as Record<string, any> | undefined)?.node;
    return node;
  }
);

export const getNodeAlignmentResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_NODE_ALIGNMENT,
  (_, __, editor) => {
    const node = (editor?.state?.selection as Record<string, any> | undefined)?.node;
    if (node) {
      return node.attrs?.containerData?.alignment;
    } else {
      return undefined;
    }
  }
);

export const getNodeSizeResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_NODE_SIZE,
  (_, __, editor) => {
    const node = (editor?.state?.selection as Record<string, any> | undefined)?.node;
    if (node) {
      return node.attrs?.containerData?.width?.size;
    } else {
      return undefined;
    }
  }
);

export const getNodeTextWrapResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_NODE_TEXT_WRAP,
  (_, __, editor) => {
    const node = (editor?.state?.selection as Record<string, any> | undefined)?.node;
    if (node) {
      return node.attrs?.containerData?.textWrap;
    } else {
      return undefined;
    }
  }
);

export const getNodeLinkDataResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_NODE_LINK_DATA,
  (_, __, editor) => {
    const node = (editor?.state?.selection as Record<string, any> | undefined)?.node;
    if (node) {
      return node.attrs?.link;
    } else {
      return undefined;
    }
  }
);

export const getUrlLinkData = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_URL_LINK_DATA,
  content => {
    if (Array.isArray(content)) {
      const node = content.find(node => {
        return node.type.name === 'text';
      });
      if (node?.marks.some(mark => mark.type.name === Decoration_Type.LINK)) {
        return node.marks[0].attrs.link;
      }
    }
    return false;
  }
);

export const getAnchorLinkData = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_ANCHOR_LINK_DATA,
  content => {
    if (Array.isArray(content)) {
      const node = content.find(node => {
        return node.type.name === 'text';
      });
      if (node?.marks.some(mark => mark.type.name === Decoration_Type.ANCHOR)) {
        return node.marks[0].attrs.anchor;
      }
    }
    return false;
  }
);

export const isUndoStackEmptyResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_UNDO_STACK_EMPTY,
  (_, __, editor) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return !editor?.can().undo();
  }
);

export const isRedoStackEmptyResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_REDO_STACK_EMPTY,
  (_, __, editor) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return !editor?.can().redo();
  }
);
